import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { clinicuiPatientLink } from '../constants/clinicui';

// The therapist's own report. No Cognito, no sidebar, no app chrome — the token
// in the URL is the whole authorisation, and it names one therapist and one
// month. Built mobile-first: freelance doctors open this on a phone.

interface PayLine {
  label: string; kind: string; status: string;
  count: number; minutes_each: number; rate_per_meeting: number; total: number;
}
interface BonusGate { label: string; actual: number; threshold: number; passed: boolean }
interface Bonus {
  relevant: boolean; eligible: boolean; pct: number; amount: number; gates: BonusGate[];
}
interface Meeting {
  day: string; time: string; kind: string; patient_type: string;
  patient_serial: string; clinic: string; status: string; amount: number;
}
interface OpenMeeting {
  day: string; time: string; kind: string; patient_type: string;
  patient_serial: string; internal_id: number; meeting_id: number; amount: number;
}
// A comment the therapist left, and where it stands — including our answer,
// which is shown HERE: the person who wrote deserves to see the reply.
interface ReportComment {
  at: string; comment: string;
  reply: string | null; replied_at: string | null; resolved: boolean;
  from_staff?: boolean;    // a note from the office, not something I wrote
}
interface Report {
  name: string; hebrew_name: string | null; month: string;
  completed_meetings: number; noshow_meetings: number; worked_hours: number;
  lines: PayLine[];
  worked_payment: number; noshow_payment: number;
  non_clinical_hours: number; non_clinical_payment: number;
  additional_payments: number; unscheduled_payment: number;
  additional_items: { item_id: string; category: string; label: string | null; amount: number; hours: number | null; rate: number | null }[];
  bonus: Bonus; total_payment: number; meetings: Meeting[];
  // The sets behind the set-bonus line, so the number has arithmetic under it.
  bonus_sets?: { serial: string; fund: string; amount: number; split: boolean;
    meetings: { day: string; cm: string }[] }[];
  approved_at: string | null;
  before_vat: boolean; respond_by: string | null;
  // The invoice step. It opens only after Basel's final sign-off — the same moment
  // the "you may issue an invoice" mail goes out (Ohad, 2026-10-06).
  basel_approved: boolean; invoice_sent_at: string | null;
  // Past-dated meetings still open in the calendar, and what closing them is worth.
  open_meetings: number; open_value: number; open_list: OpenMeeting[];
  comments: ReportComment[];
}

const MON_HE = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי',
  'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const KIND_HE: Record<string, string> = {
  intake: 'אינטייק', followup: 'מעקב', double: 'מעקב כפול', reintake: 'ריאינטייק',
};
const ils = (n: number) => '₪' + (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (n: number, d = 0) =>
  Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const monthLabel = (m: string) => `${MON_HE[Number(m.slice(5)) - 1]} ${m.slice(0, 4)}`;
// The clinical system's patient card; `id` is the internal numeric id, the same
// deep-link the meetings explorer and the patient drill use.


export default function SalaryReport() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showMeetings, setShowMeetings] = useState(false);
  const [showSets, setShowSets] = useState(false);
  const [showOpen, setShowOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [mode, setMode] = useState<'idle' | 'commenting' | 'done-approved' | 'done-commented'>('idle');
  const [submitting, setSubmitting] = useState(false);
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceDone, setInvoiceDone] = useState(false);
  const [invoiceErr, setInvoiceErr] = useState<string | null>(null);

  const submit = useCallback(async (approve: boolean) => {
    setSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/exec/salary/report/respond`,
        { token, approve, comment: approve ? undefined : comment });
      setMode(approve ? 'done-approved' : 'done-commented');
      load();   // the comment thread below should show what was just written
    } catch {
      setSubmitting(false);   // stay on the form so the therapist can retry
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, comment]);

  // The last step the therapist takes. One way and timestamped: it tells Lee an
  // invoice is on its way, and un-telling her is not a thing a toggle should allow.
  const markInvoiceSent = useCallback(async () => {
    setSubmitting(true);
    setInvoiceErr(null);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/exec/salary/report/respond`,
        { token, invoice_sent: true, invoice_number: invoiceNo.trim() || undefined });
      setInvoiceDone(true);
      load();
    } catch (e) {
      const d = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setInvoiceErr(d || 'לא הצלחנו לרשום את הסימון — נסו שוב בעוד רגע');
    } finally { setSubmitting(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, invoiceNo]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/exec/salary/report`,
        { params: { token } });
      setReport(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'לא הצלחנו לטעון את הדוח.');
    }
  }, [token]);

  useEffect(() => {
    if (!token) { setError('קישור חסר.'); return; }
    load();
  }, [token, load]);

  if (error) {
    return (
      <div dir="rtl" className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-sm text-center">
          <div className="text-gray-900 font-medium mb-1">הקישור אינו זמין</div>
          <div className="text-sm text-gray-500">{error}</div>
          <div className="text-xs text-gray-500 mt-3">לבירור אפשר לפנות אלינו.</div>
        </div>
      </div>
    );
  }
  if (!report) {
    return <div dir="rtl" className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500 text-sm">טוען…</div>;
  }

  const r = report;
  const meetingsPay = r.worked_payment + r.noshow_payment;

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50 pb-10">
      <div className="max-w-lg mx-auto px-4 pt-6 space-y-4">

        {/* Who and when, then the number. Nothing above it. */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="text-sm text-gray-500">{r.hebrew_name || r.name}</div>
          <div className="text-xs text-gray-500 mb-4">דוח חודשי · {monthLabel(r.month)}</div>
          <div className="text-xs text-gray-500 mb-1">
            סה״כ לתשלום{r.before_vat && ' (לפני מע״מ)'}
          </div>
          <div className="text-4xl font-bold text-gray-900 tabular-nums tracking-tight">
            {ils(r.total_payment)}
          </div>
          <div className="text-xs text-gray-500 mt-3 tabular-nums">
            {num(r.completed_meetings)} פגישות · {num(r.worked_hours, 2)} שעות
            {r.noshow_meetings > 0 && ` · ${num(r.noshow_meetings)} no-show`}
          </div>
        </div>

        {/* Money that is not on the payslip only because a meeting was never closed.
            Placed right under the total, phrased as something to claim rather than
            something lost: the point is that the therapist goes and closes them
            (Ohad, 2026-07-28). Nothing is withheld — an open meeting simply is not
            payable until it is marked as held. */}
        {r.open_meetings > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-5">
            <div className="text-sm font-semibold text-amber-900">
              יש לך {num(r.open_meetings)} פגישות שלא נסגרו
            </div>
            {/* The list opens from beside the amount (Ohad, 2026-07-28) — "13 meetings"
                is only actionable once you know WHICH thirteen. */}
            <div className="flex items-end justify-between gap-3 mt-1">
              <div className="text-2xl font-bold text-amber-900 tabular-nums">
                {ils(r.open_value)}
              </div>
              <button
                onClick={() => setShowOpen((v) => !v)}
                className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
              >
                לסגירת הפגישות {showOpen ? '▴' : '▾'}
              </button>
            </div>

            {showOpen && (
              <div className="mt-3 border-t border-amber-200 pt-3 space-y-1.5">
                {r.open_list.map((m, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-xs text-amber-900">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="tabular-nums shrink-0" dir="ltr">{m.day} {m.time}</span>
                      <span className="text-amber-700 shrink-0">{KIND_HE[m.kind] ?? m.kind}</span>
                      {/* Straight into the patient's card in the clinical system — the
                          therapist closes the meeting there, which is the whole point
                          of showing the number. Serial only, never a name. */}
                      {m.internal_id > 0 ? (
                        <a href={clinicuiPatientLink(m.internal_id, m.meeting_id)}
                           target="_blank" rel="noreferrer"
                           className="truncate underline underline-offset-2 hover:text-amber-600"
                           dir="ltr" title="פתח את כרטיס המטופל">
                          {m.patient_serial}
                        </a>
                      ) : (
                        /* a demo patient has no clinicui card — serial as plain text */
                        <span className="truncate" dir="ltr">{m.patient_serial}</span>
                      )}
                    </div>
                    <span className="tabular-nums shrink-0">{ils(m.amount)}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="text-xs text-amber-800 mt-3 leading-relaxed">
              פגישות שהמועד שלהן עבר ועדיין מסומנות כפעילות ביומן. הסכום הזה
              <b> אינו כלול</b> בסה״כ שלמעלה, והוא ייכנס לתשלום ברגע שהן יסומנו כבוצעו.
            </div>
          </div>
        )}

        {/* The arithmetic. */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="text-sm font-medium text-gray-900 mb-3">איך זה מחושב</div>
          <div className="space-y-2">
            {/* Label and its arithmetic stack, so a long Hebrew label never
                pushes the amount off a narrow screen. */}
            {r.lines.map((ln, i) => (
              <div key={i} className="flex items-start justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div className="text-gray-700">
                    {ln.label}
                    {ln.kind === 'double' && <span className="text-blue-600"> ×2</span>}
                  </div>
                  <div className="text-xs text-gray-500 tabular-nums">
                    {ln.count} × {ils(ln.rate_per_meeting)}
                  </div>
                </div>
                <span className="font-medium text-gray-900 tabular-nums whitespace-nowrap shrink-0">
                  {ils(ln.total)}
                </span>
              </div>
            ))}

            <div className="border-t border-gray-200 pt-2 flex justify-between text-sm">
              <span className="text-gray-500">סה״כ פגישות</span>
              <span className="font-medium tabular-nums">{ils(meetingsPay)}</span>
            </div>

            {r.non_clinical_payment > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">שעות לא-קליניות ({num(r.non_clinical_hours, 2)})</span>
                <span className="font-medium tabular-nums">{ils(r.non_clinical_payment)}</span>
              </div>
            )}
            {r.unscheduled_payment > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">שעות לא מתוזמנות</span>
                <span className="font-medium tabular-nums">{ils(r.unscheduled_payment)}</span>
              </div>
            )}
            {r.additional_payments > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">תשלומים נוספים</span>
                <span className="font-medium tabular-nums">{ils(r.additional_payments)}</span>
              </div>
            )}
            {(r.additional_items ?? []).filter((it) => it.amount > 0).map((it) => (
              <div key={it.item_id} className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {it.label || 'תוספת'}
                  {it.hours && it.rate ? ` (${num(it.hours, 2)} × ${ils(it.rate)})` : ''}
                </span>
                <span className="font-medium tabular-nums">{ils(it.amount)}</span>
              </div>
            ))}
            {r.bonus.eligible && r.bonus.amount > 0 && (
              <div className="flex justify-between text-sm text-green-700">
                <span>בונוס{r.bonus.pct ? ` (${r.bonus.pct}%)` : ''}</span>
                <span className="font-medium tabular-nums">{ils(r.bonus.amount)}</span>
              </div>
            )}

            <div className="border-t-2 border-gray-300 pt-2 flex justify-between">
              <span className="font-semibold text-gray-900">
                סה״כ לתשלום{r.before_vat && <span className="font-normal text-gray-500 text-xs"> (לפני מע״מ)</span>}
              </span>
              <span className="font-bold text-gray-900 tabular-nums">{ils(r.total_payment)}</span>
            </div>
          </div>
        </div>

        {/* Why the bonus did or did not land — so the question never has to be
            asked by phone. */}
        {r.bonus.relevant && (
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="text-sm font-medium text-gray-900 mb-1">
              בונוס — {r.bonus.eligible ? `זכאי (${r.bonus.pct}%)` : 'לא זכאי החודש'}
            </div>
            <div className="text-xs text-gray-500 mb-3">
              הבונוס ניתן כאשר כל שלושת התנאים מתקיימים.
            </div>
            <div className="space-y-1.5">
              {r.bonus.gates.map((g, i) => (
                <div key={i} className="flex items-center gap-2 text-sm flex-wrap">
                  <span className={g.passed ? 'text-green-600' : 'text-red-500'}>
                    {g.passed ? '✓' : '✗'}
                  </span>
                  <span className="text-gray-600">{g.label}</span>
                  <span className="text-gray-900 tabular-nums">{num(g.actual, 2)}</span>
                  <span className="text-gray-500 text-xs">(נדרש {num(g.threshold, 0)})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* The meetings behind it. */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setShowMeetings((v) => !v)}
            className="w-full text-right px-5 py-4 text-sm font-medium text-gray-900 flex justify-between items-center"
          >
            <span>הפגישות שלי ({r.meetings.length}) <span className="font-normal text-gray-500 text-xs">— לחצו לפירוט מלא</span></span>
            <span className="text-gray-500">{showMeetings ? '▾' : '▸'}</span>
          </button>
          {showMeetings && (
            <div className="border-t border-gray-100 max-h-96 overflow-y-auto">
              {r.meetings.map((m, i) => (
                <div key={i} className="px-5 py-2.5 border-b border-gray-50 flex items-baseline justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <div className="text-gray-900 tabular-nums">
                      {m.day.slice(8)}/{m.day.slice(5, 7)} <span className="text-gray-500">{m.time}</span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {KIND_HE[m.kind] ?? m.kind}
                      {m.kind === 'double' && <span className="text-blue-600"> ×2</span>}
                      {' · '}{m.patient_serial || '—'}
                      {m.status === 'noshow' && <span className="text-amber-600"> · no-show</span>}
                    </div>
                  </div>
                  <div className="tabular-nums text-gray-900 whitespace-nowrap">{ils(m.amount)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* The sets behind the bonus line — its own card rather than an accordion
            inside the totals, which would push the bottom line down the page. The
            set bonus is the one line people dispute (Anis counted 30 against 26,
            2026-09-04), so it opens into the arithmetic instead of into a mail
            to Basel. */}
        {(r.bonus_sets ?? []).length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <button
              onClick={() => setShowSets((v) => !v)}
              className="w-full text-right px-5 py-4 text-sm font-medium text-gray-900 flex justify-between items-center"
            >
              <span>בונוס סטים ({(r.bonus_sets ?? []).length}) <span className="font-normal text-gray-500 text-xs">— לחצו לפירוט מלא</span></span>
              <span className="text-gray-500">{showSets ? '▾' : '▸'}</span>
            </button>
            {showSets && (
              <div className="border-t border-gray-100 max-h-96 overflow-y-auto">
                {(r.bonus_sets ?? []).map((st, i) => (
                  <div key={i} className="px-5 py-2.5 border-b border-gray-50 flex items-baseline justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <div className="text-gray-900">
                        {st.serial}
                        <span className="text-gray-500 text-xs"> · {st.fund === 'Clalit' ? 'כללית' : 'מכבי'}</span>
                        {st.split && <span className="text-amber-600 text-xs"> · מפוצל בין מטפלים</span>}
                      </div>
                      <div className="text-xs text-gray-500 tabular-nums truncate">
                        {st.meetings.map((m) => `${m.day.slice(8)}/${m.day.slice(5, 7)}`).join(' ← ')}
                      </div>
                    </div>
                    <div className="tabular-nums text-gray-900 whitespace-nowrap">{ils(st.amount)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* The therapist's comments, with our answers. A reply that only closes a
            badge on the admin side answers nobody — it belongs here, on the page
            of the person who wrote (Ohad's test, 2026-08-03). */}
        {r.comments.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
            <div className="text-sm font-medium text-gray-900">הערות והתכתבות</div>
            {r.comments.map((c, i) => c.from_staff ? (
              /* A note the office wrote to me — opens the thread, nothing to resolve. */
              <div key={i} className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-1">
                <div className="text-[11px] font-medium text-blue-700">הערה מהצוות · {c.at}</div>
                <div className="text-sm text-blue-900 whitespace-pre-wrap">{c.comment}</div>
              </div>
            ) : (
              <div key={i} className="border border-gray-100 rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm text-gray-800 whitespace-pre-wrap">{c.comment}</div>
                  {c.resolved
                    ? <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">✓ טופל</span>
                    : <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">בטיפול</span>}
                </div>
                <div className="text-[11px] text-gray-500">{c.at}</div>
                {c.reply && (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5">
                    <div className="text-[11px] font-medium text-blue-700 mb-0.5">תשובה מהצוות{c.replied_at ? ` · ${c.replied_at}` : ''}</div>
                    <div className="text-sm text-blue-900 whitespace-pre-wrap">{c.reply}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* The invoice step. Opens on Basel's sign-off, not on the therapist's own
            approval: the "you may invoice" mail is sent by Basel, and a page that
            answered it with no button would be worse than no mail (Ohad, 2026-10-06).
            Shown ABOVE the approve box, because once Basel has signed off this is
            the only thing left for the therapist to do. */}
        {r.basel_approved && (r.invoice_sent_at || invoiceDone ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-3 text-center">
            <div className="text-green-800 font-medium">✓ רשמנו שהחשבונית נשלחה, תודה.</div>
            {r.invoice_sent_at && (
              <div className="text-xs text-green-700 mt-1">
                סומן ב-{new Date(r.invoice_sent_at).toLocaleDateString('he-IL')}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white border border-blue-200 rounded-xl p-5 mb-3">
            <div className="text-sm font-medium text-gray-800 mb-1">הדוח אושר סופית — אפשר להוציא חשבונית</div>
            <div className="text-sm text-gray-600 mb-3">
              אחרי ששלחתם אותה, סמנו כאן. כך הנהלת החשבונות יודעת לאיזו חשבונית לחכות.
            </div>
            <div className="flex gap-2">
              <input
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
                placeholder="מספר חשבונית (לא חובה)"
                className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-3 text-sm"
              />
              <button
                onClick={markInvoiceSent}
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg px-5 py-3 whitespace-nowrap"
              >
                {submitting ? 'שולח…' : 'שלחתי חשבונית'}
              </button>
            </div>
            {invoiceErr && <div className="mt-2 text-sm text-red-600">{invoiceErr}</div>}
          </div>
        ))}

        {/* Approve or object. */}
        {mode === 'done-approved' ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center">
            <div className="text-green-800 font-medium">✓ קיבלנו את אישור הדוח, תודה.</div>
          </div>
        ) : mode === 'done-commented' ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center">
            <div className="text-green-800 font-medium">תודה, נרשם.</div>
            <div className="text-xs text-green-700 mt-1">ההערה הועברה לטיפול — התשובה תופיע כאן, בדוח.</div>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            {mode === 'idle' && r.approved_at ? (
              <>
                <div className="text-sm font-medium text-green-700 mb-3">✓ הדוח אושר. תודה!</div>
                <button
                  onClick={() => setMode('commenting')}
                  className="w-full border border-gray-300 text-gray-700 text-sm font-medium rounded-lg py-3"
                >
                  יש לי הערה
                </button>
              </>
            ) : mode === 'idle' ? (
              <>
                <div className="text-sm text-gray-600 mb-1">
                  נא לעבור על הדוח ולאשר. אם משהו לא מסתדר — כתבו לנו ונבדוק.
                </div>
                {r.respond_by && (
                  <div className="text-sm font-medium text-amber-700 mb-3">
                    נא להשיב עד {r.respond_by}
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => submit(true)}
                    disabled={submitting}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg py-3"
                  >
                    {submitting ? 'שולח…' : 'מאשר את הדוח'}
                  </button>
                  <button
                    onClick={() => setMode('commenting')}
                    className="flex-1 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg py-3"
                  >
                    יש לי הערה
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-sm text-gray-600 mb-2">מה לא מסתדר?</div>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  className="w-full text-sm border border-gray-300 rounded-lg p-3 mb-2"
                  placeholder="לדוגמה: פגישה מה-12 בחודש לא מופיעה"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => submit(false)}
                    disabled={!comment.trim() || submitting}
                    className="flex-1 bg-blue-600 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg py-3"
                  >
                    {submitting ? 'שולח…' : 'שליחה'}
                  </button>
                  <button
                    onClick={() => setMode('idle')}
                    className="px-4 border border-gray-300 text-gray-600 text-sm rounded-lg py-3"
                  >
                    ביטול
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <p className="text-xs text-gray-500 text-center px-4">
          {r.before_vat && 'הסכומים אינם כוללים מע״מ. '}
          התשלום מחושב לפי פגישה — כל פגישה לפי התעריף שסוכם, ללא תלות באורך שנקבע ביומן.
        </p>
      </div>
    </div>
  );
}
